import { useCallback, useEffect, useMemo, useState } from 'react';
import { AvailableProgram } from './useAvailablePrograms';
import { LinkedProgram } from './useLinkedPrograms';
import { LocalLinkedProgram } from './types';

// AIDEV-NOTE: Manages the modal's local staging state — newly added programs are marked
// `staged: true` until Save persists them; existing programs can be toggled into programsToUnlink.

interface UseProgramSelectionResult {
  editCounter: number;
  handleAddProgram: (program: AvailableProgram) => void;
  handleRemoveStaged: (programId: number) => void;
  handleToggleUnlink: (programId: number) => void;
  hasChanges: boolean;
  linkedProgramIds: Set<number>;
  localPrograms: LocalLinkedProgram[];
  programsToLink: number[];
  programsToUnlink: Set<number>;
}

const useProgramSelection = (linkedPrograms: LinkedProgram[]): UseProgramSelectionResult => {
  const [localPrograms, setLocalPrograms] = useState<LocalLinkedProgram[]>([]);
  const [programsToUnlink, setProgramsToUnlink] = useState<Set<number>>(new Set());
  const [editCounter, setEditCounter] = useState(0);

  useEffect(() => {
    setLocalPrograms(linkedPrograms);
    setProgramsToUnlink(new Set());
    // Intentionally not bumping editCounter — this is server-state arrival, not a user edit.
  }, [linkedPrograms]);

  const linkedProgramIds = useMemo(
    () => new Set(localPrograms.map(p => p.id)),
    [localPrograms],
  );

  const programsToLink = useMemo(
    () => localPrograms.filter(p => p.staged).map(p => p.id),
    [localPrograms],
  );

  const hasChanges = programsToLink.length > 0 || programsToUnlink.size > 0;

  const bumpEdit = useCallback(() => setEditCounter(c => c + 1), []);

  const handleAddProgram = useCallback((program: AvailableProgram) => {
    setLocalPrograms(prev => [
      ...prev,
      { class_par_exists: false, id: program.id, name: program.name, staged: true },
    ]);
    bumpEdit();
  }, [bumpEdit]);

  const handleRemoveStaged = useCallback((programId: number) => {
    setLocalPrograms(prev => prev.filter(p => p.id !== programId));
    bumpEdit();
  }, [bumpEdit]);

  const handleToggleUnlink = useCallback((programId: number) => {
    setProgramsToUnlink((prev) => {
      const next = new Set(prev);
      if (next.has(programId)) {
        next.delete(programId);
      } else {
        next.add(programId);
      }
      return next;
    });
    bumpEdit();
  }, [bumpEdit]);

  return {
    editCounter,
    handleAddProgram,
    handleRemoveStaged,
    handleToggleUnlink,
    hasChanges,
    linkedProgramIds,
    localPrograms,
    programsToLink,
    programsToUnlink,
  };
};

export default useProgramSelection;
